import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { usePublicSettings } from "@/lib/settings";
import { api } from "@/lib/api";
import { Phone, Mail, MapPin, Send, Loader2, Facebook, Instagram, Globe, Wrench, ShieldCheck } from "lucide-react";

const TikTok = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.69a8.16 8.16 0 0 0 4.77 1.52V7.78a4.85 4.85 0 0 1-1.84-1.09Z" />
  </svg>
);

const formatTel = (raw) => (raw || "").replace(/[^+\d]/g, "");

const Contact = () => {
  const settings = usePublicSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll get back to you shortly");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-spark-floor" data-testid="contact-page">
      <Navbar />

      <div className="bg-spark-teal-deep text-white border-b-4 border-spark-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="label-mono text-spark-yellow mb-2">Get in touch</p>
            <h1 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95]">Contact Us</h1>
            <p className="mt-4 text-slate-300 max-w-xl">Have a question or need a quote? Send us a message or pick up the phone — we usually reply within a few hours.</p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-12 gap-8">
        {/* Contact info */}
        <aside className="lg:col-span-5 space-y-6">
          <div className="surface-card p-6 sm:p-8 space-y-5">
            <p className="label-mono text-spark-teal">{settings.business_name}</p>
            {settings.phone && (
              <a href={`tel:${formatTel(settings.phone)}`} className="flex items-start gap-4 group" data-testid="contact-phone">
                <div className="size-11 rounded-sm bg-spark-teal-deep text-spark-yellow flex items-center justify-center shrink-0 group-hover:bg-spark-teal transition"><Phone className="size-5" /></div>
                <div>
                  <p className="label-mono">Phone</p>
                  <p className="font-display uppercase tracking-wider text-lg">{settings.phone}</p>
                </div>
              </a>
            )}
            {settings.email && (
              <a href={`mailto:${settings.email}`} className="flex items-start gap-4 group break-all" data-testid="contact-email">
                <div className="size-11 rounded-sm bg-spark-teal-deep text-spark-yellow flex items-center justify-center shrink-0 group-hover:bg-spark-teal transition"><Mail className="size-5" /></div>
                <div>
                  <p className="label-mono">Email</p>
                  <p className="font-semibold">{settings.email}</p>
                </div>
              </a>
            )}
            {settings.address_line && (
              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="size-11 rounded-sm bg-spark-teal-deep text-spark-yellow flex items-center justify-center shrink-0"><MapPin className="size-5" /></div>
                <div>
                  <p className="label-mono">Address</p>
                  <p className="font-semibold">{settings.address_line}</p>
                  {(settings.city || settings.region || settings.country) && (
                    <p className="text-spark-slate text-sm">{[settings.city, settings.region, settings.country].filter(Boolean).join(", ")}</p>
                  )}
                </div>
              </div>
            )}

            {(settings.facebook_url || settings.instagram_url || settings.tiktok_url || settings.google_maps_url) && (
              <div className="border-t border-spark-line pt-5">
                <p className="label-mono mb-3">Find us online</p>
                <div className="flex flex-wrap gap-2">
                  {settings.facebook_url && (
                    <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-spark-line hover:border-spark-teal hover:text-spark-teal px-3 py-2 rounded-sm label-mono transition" data-testid="contact-facebook">
                      <Facebook className="size-4" /> Facebook
                    </a>
                  )}
                  {settings.instagram_url && (
                    <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-spark-line hover:border-spark-teal hover:text-spark-teal px-3 py-2 rounded-sm label-mono transition" data-testid="contact-instagram">
                      <Instagram className="size-4" /> Instagram
                    </a>
                  )}
                  {settings.tiktok_url && (
                    <a href={settings.tiktok_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-spark-line hover:border-spark-teal hover:text-spark-teal px-3 py-2 rounded-sm label-mono transition" data-testid="contact-tiktok">
                      <TikTok className="size-4" /> TikTok
                    </a>
                  )}
                  {settings.google_maps_url && (
                    <a href={settings.google_maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-spark-line hover:border-spark-teal hover:text-spark-teal px-3 py-2 rounded-sm label-mono transition" data-testid="contact-google">
                      <Globe className="size-4" /> Google Maps
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="bg-spark-yellow text-spark-ink rounded-md p-5 sm:p-6 flex items-start gap-3">
            <ShieldCheck className="size-6 shrink-0" />
            <div>
              <p className="font-display uppercase tracking-tight font-bold text-lg leading-tight">Mobile service in Horowhenua</p>
              <p className="text-sm mt-1">Travel fees may apply outside the region — confirmed up front, no surprises.</p>
            </div>
          </div>

          <Link to="/book" className="btn-primary w-full" data-testid="contact-book-btn">
            <Wrench className="size-4" /> Book a Repair
          </Link>
        </aside>

        {/* Contact form */}
        <form onSubmit={submit} className="lg:col-span-7 surface-card p-6 sm:p-8 space-y-5" data-testid="contact-form">
          <div>
            <p className="label-mono text-spark-teal mb-1">Send a message</p>
            <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold">Drop us a line</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-mono mb-2 inline-block">Name <span className="text-spark-teal">*</span></label>
              <input className="input-style" value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="contact-name" />
            </div>
            <div>
              <label className="label-mono mb-2 inline-block">Email <span className="text-spark-teal">*</span></label>
              <input type="email" className="input-style" value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="contact-email-input" />
            </div>
            <div>
              <label className="label-mono mb-2 inline-block">Phone</label>
              <input className="input-style" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="label-mono mb-2 inline-block">Subject <span className="text-spark-teal">*</span></label>
              <input className="input-style" placeholder="e.g. Battery quote, mobile service" value={form.subject} onChange={(e) => setField("subject", e.target.value)} data-testid="contact-subject" />
            </div>
          </div>
          <div>
            <label className="label-mono mb-2 inline-block">Message <span className="text-spark-teal">*</span></label>
            <textarea rows={6} className="input-style resize-none" placeholder="Tell us what's going on…" value={form.message} onChange={(e) => setField("message", e.target.value)} data-testid="contact-message" />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-spark-slate">We respect your privacy — your details are never shared.</p>
            <button type="submit" disabled={sending} className="btn-primary" data-testid="contact-submit">
              {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} Send Message
            </button>
          </div>
          {sent && (
            <div className="bg-spark-teal-light border border-spark-teal text-spark-ink rounded-md p-4 text-sm" data-testid="contact-success">
              ✓ Message received! We'll be in touch shortly at the email/phone you provided.
            </div>
          )}
        </form>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
